import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { AppKit } from '@reown/appkit';
import * as Web3 from '@solana/web3.js';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { presaleSol } from '../../shared/constants/wallet-connect.const';
import { ReownService } from '../connection-services/reown.service';

type SolanaWeb3Module = typeof import('@solana/web3.js')


@Injectable({
    providedIn: 'root',
})
export class SolanaInitialisationService {
    private reownWalletConnector!: AppKit
    public $solInitilisation = new BehaviorSubject<boolean>(false);
    public presaleAccountAddress: any;
    public stateInfoAddress: any;
    public program: any;
    public provider: any;
    public connection!: Web3.Connection;

    solWalletPubkey: any;
    solanaWeb3: any;
    coralXyz: any;
    programId?: any;
    associate_programId?: any;
    BN?: any;
    lamports_per_sol?: any;
    systemProgram?: any;
    web3PublicKey?: any;
    ConfirmOptions?: any;

    USDT_TOKEN_ADDRESS: any; // Public key of the USDT token. Insert the USDT token address
    USDC_TOKEN_ADDRESS: any; // Public key of the USDT token. Insert the USDT token address

    price = 0;
    readonly publicKey = signal<Web3.PublicKey | null>(null);

    constructor(
        @Inject(PLATFORM_ID) private _platformId: Object,
        private http: HttpClient,
        private reownService: ReownService
    ) {
        this.initializeSolPackage();
        this.reownService.$connectionItems.subscribe((item) => {
            if (item.appKitInstance) {
                this.reownWalletConnector = item.appKitInstance as AppKit
            }
            if (item.walletAddressSOL) {
                this.buildAnchorProvider();
                this.solWalletPubkey = new this.web3PublicKey(item.walletAddressSOL)
            }
        })
    }

    async initializeSolPackage() {
        this.solanaWeb3 = Web3;
        const { ConfirmOptions, Connection, PublicKey, LAMPORTS_PER_SOL, SystemProgram } = this.solanaWeb3;
        this.ConfirmOptions = ConfirmOptions;
        this.web3PublicKey = PublicKey;
        this.programId = new PublicKey(environment.sol.presaleAddress);
        this.USDT_TOKEN_ADDRESS = new PublicKey(environment.sol.usdtTokenAddress); // Public key of the USDT token. Insert the USDT token address
        this.USDC_TOKEN_ADDRESS = new PublicKey(environment.sol.usdcTokenAddress); // Public key of the USDT token. Insert the USDT token address
        this.lamports_per_sol = LAMPORTS_PER_SOL;
        this.systemProgram = SystemProgram;

        this.http.get('https://api.diadata.org/v1/assetQuotation/Solana/0x0000000000000000000000000000000000000000').subscribe((res: any) => {
            this.price = res.Price;
        });

        this.connection = new Connection(environment.sol.SolRpcUrl, 'processed');
        const coralXyz = await import('@coral-xyz/anchor')
        this.coralXyz = coralXyz;
        const { ASSOCIATED_PROGRAM_ID, BN } = this.coralXyz;
        this.BN = BN;
        this.associate_programId = ASSOCIATED_PROGRAM_ID;
        const DUMMY_PUBLIC_KEY = new PublicKey('11111111111111111111111111111111'); // System Program address
        const dummyWallet = {
            publicKey: DUMMY_PUBLIC_KEY,
            signTransaction: async (tx: Web3.Transaction) => tx,
            signAllTransactions: async (txs: Web3.Transaction) => txs,
        };
        this.provider = new this.coralXyz.AnchorProvider(this.connection, dummyWallet, ConfirmOptions);
        this.setProgram();
        // In the background, verify primary; if it fails, switch to backup
        this.probeRpc(environment.sol.SolRpcUrl).then((healthyConn) => {
            console.log("first rpc")
            // primary ok -> ensure we’re using the probed instance
            this.connection = healthyConn;

            this.reinitProvider();         // rebuild provider/program if Anchor already loaded

        }).catch(() => {
            console.log("second rpc")
            // primary bad -> try backup
            this.probeRpc(environment.sol.SolBackupRpcUrl).then((healthyConn) => {
                this.connection = healthyConn; // swap
                this.reinitProvider();         // rebuild provider/program if Anchor already loaded

            }).catch(() => {
                console.log("third?")
                // both failed: keep the default primary; you may want to surface a warning
                this.connection = new Connection(environment.sol.SolRpcUrl, 'processed');
                this.reinitProvider();         // rebuild provider/program if Anchor already loaded
            });
        });
    }


    // Probe an RPC; resolve with a healthy Connection or reject on timeout/failure
    private probeRpc(url: string, timeoutMs = 3000) {
        const { Connection } = this.solanaWeb3;
        const testConn = new Connection(url, 'processed');
        const timeout = new Promise<never>((_, rej) => setTimeout(() => rej('timeout'), timeoutMs));
        return Promise.race([testConn.getLatestBlockhash('processed'), timeout])
            .then(() => testConn);
    }

    // Rebuild Anchor provider/program if/when we swap connections
    private reinitProvider() {
        const { ASSOCIATED_PROGRAM_ID, BN } = this.coralXyz;
        this.BN = BN;
        this.associate_programId = ASSOCIATED_PROGRAM_ID;
        //@ts-ignore
        this.provider = new this.coralXyz.AnchorProvider(this.connection, window?.solana, this.ConfirmOptions);

        this.setProgram();
    }

    /**
     * Setting program which will required for every transaction
     * ONLY runs when all dependencies (coralXyz, provider) are ready.
     */
    setProgram(): void {
        if (!this.coralXyz || !this.provider) {
            // If the dependencies are not ready, don't run.
            // The caller must ensure this function is only called when ready.
            console.warn("Attempted to setProgram but coralXyz or provider is not ready.");
            return;
        }
        // This is the core logic that creates the Anchor program.
        this.program = new this.coralXyz.Program(presaleSol, this.programId, this.provider);
        this.getRequiredAddress();
    }

    async loadCoralXyz() {
        if (!this.coralXyz) {
            const coralXyz = await import('@coral-xyz/anchor');
            this.coralXyz = coralXyz;
            const { ASSOCIATED_PROGRAM_ID, BN } = this.coralXyz;
            this.BN = BN;
            this.associate_programId = ASSOCIATED_PROGRAM_ID;
        }
    }


    /**
     * fething required address to get all contract data
     */

    async getRequiredAddress() {
        // --------------------- PublicKey.findProgramAddressSync => [PublicKey, Size] ----------------------------
        // ======= through this address we can get the necessary data of presale account (METADATA FOR PRESALE ACCOUNT) ==========
        this.presaleAccountAddress = this.web3PublicKey.findProgramAddressSync([Buffer.from('presale_account')], this.programId);

        // ======= through this address we can get the necessary data of smartContract ==========
        this.stateInfoAddress = this.web3PublicKey.findProgramAddressSync([Buffer.from('state_info')], this.programId);
        this.$solInitilisation.next(true);
    }


    buildAnchorProvider() {
        try {
            const connection = this.connection;

            // Ask AppKit for the active Solana wallet provider
            const walletProvider = this.reownWalletConnector?.getProvider("solana") as any;
            if (!walletProvider) throw new Error('No Solana wallet connected')
            // You can get address separately, then expose an Anchor-compatible wallet
            const address = this.reownWalletConnector.getAddress("solana") // when Solana is the active network
            if (!address) throw new Error('No Solana address')

            const anchorWallet = {
                publicKey: new this.web3PublicKey(address),
                signTransaction: (tx: Web3.Transaction) => walletProvider.signTransaction(tx),
                signAllTransactions: (txs: Web3.Transaction[]) => walletProvider.signAllTransactions(txs),
                signAndSendTransaction: (txs: Web3.Transaction[]) => walletProvider.signAndSendTransaction(txs)
            }
            const provider = new this.coralXyz.AnchorProvider(connection, anchorWallet as any, this.coralXyz?.AnchorProvider.defaultOptions());
            if (provider) this.provider = provider;
            this.setProgram()
        } catch (error) {
            console.log(error, "---error in provider");

        }
    }

}
